/**
 * ETL v2 — Expression parser.
 *
 * Self-contained Pratt parser. Consumes a source slice (a single line of
 * expression text — `if`/`elseif`/`unless`/`when`/`until`/`forEach`/
 * `expect EXPR`/`set X = …` carve such a slice out of the surrounding
 * step line) and produces an {@link Expr} AST.
 *
 * The grammar:
 *
 * ```
 *   expr      = orExpr
 *   orExpr    = andExpr ('||' andExpr)*
 *   andExpr   = notExpr ('&&' notExpr)*
 *   notExpr   = '!' notExpr | cmpExpr
 *   cmpExpr   = addExpr (('=='|'!='|'<'|'<='|'>'|'>=') addExpr)?
 *   addExpr   = mulExpr (('+'|'-') mulExpr)*
 *   mulExpr   = unary (('*'|'/'|'%') unary)*
 *   unary     = '-' unary | postfix
 *   postfix   = primary ('.' IDENT | '[' expr ']' | '(' argList? ')')*
 *   primary   = STRING | NUMBER | 'true'|'false'|'null'
 *             | IDENT | '(' expr ')'
 * ```
 *
 * Regex and locator literals are reserved for a later phase — they only
 * appear inside `expect TEXT @x matches /…/` and `exists(@…)`, neither
 * of which is wired up yet. Adding them is a localized extension of
 * {@link readPrimary}.
 *
 * The parser is intentionally strict: unknown identifiers ARE allowed at
 * parse time (they may bind at runtime) but unknown function names are
 * rejected immediately because the function-call set is closed.
 */

import {
  EXPR_BUILTIN_ARITY,
  EXPR_BUILTIN_FNS,
  ExprParseError,
  type Expr,
  type ExprBinaryOp,
  type ExprBuiltinFn,
  type ExprUnaryOp,
} from './expr-types';
import type { EtlLimits } from './limits';
import { DEFAULT_LIMITS } from './limits';

/* ─────────────────────────── lexer ───────────────────────────────── */

type ExprTok =
  | { kind: 'num'; value: number; col: number }
  | { kind: 'str'; value: string; col: number }
  | { kind: 'ident'; value: string; col: number }
  | { kind: 'punct'; value: string; col: number }
  | { kind: 'eof'; col: number };

const KEYWORD_LITERALS: Readonly<Record<string, true | false | null>> = {
  true: true,
  false: false,
  null: null,
};

const PUNCT_2: ReadonlyArray<string> = ['==', '!=', '<=', '>=', '&&', '||'];
const PUNCT_1: ReadonlyArray<string> = [
  '!', '<', '>', '+', '-', '*', '/', '%',
  '(', ')', '[', ']', ',', '.',
];

function tokenizeExpr(source: string, line: number, baseCol: number): ExprTok[] {
  const tokens: ExprTok[] = [];
  const len = source.length;
  let i = 0;

  while (i < len) {
    const c = source.charCodeAt(i);
    // whitespace
    if (c === 0x20 || c === 0x09) { i++; continue; }
    const col = baseCol + i;

    // string
    if (c === 0x22 /* " */) {
      let j = i + 1;
      let value = '';
      while (j < len) {
        const cc = source.charCodeAt(j);
        if (cc === 0x22) { j++; break; }
        if (cc === 0x5c /* \ */ && j + 1 < len) {
          const esc = source[j + 1];
          value += esc === 'n' ? '\n' : esc === 't' ? '\t' : esc;
          j += 2;
          continue;
        }
        if (cc === 0x0a || cc === 0x0d) {
          throw new ExprParseError('Unterminated string in expression', line, col);
        }
        value += source[j];
        j++;
      }
      if (j > len || source[j - 1] !== '"') {
        // We exited because j === len without seeing a closing quote.
        if (source.length === 0 || source.charCodeAt(j - 1) !== 0x22) {
          // Re-check: did we ever see a closing quote?
          // (Defensive; the loop above only breaks on `cc === 0x22`.)
        }
      }
      tokens.push({ kind: 'str', value, col });
      i = j;
      continue;
    }

    // number (decimal integer or simple float)
    if (c >= 0x30 && c <= 0x39) {
      let j = i;
      while (j < len && source.charCodeAt(j) >= 0x30 && source.charCodeAt(j) <= 0x39) j++;
      if (j < len && source.charCodeAt(j) === 0x2e /* . */) {
        const beforeDot = j;
        j++;
        while (j < len && source.charCodeAt(j) >= 0x30 && source.charCodeAt(j) <= 0x39) j++;
        if (j === beforeDot + 1) {
          throw new ExprParseError('Trailing decimal point in number literal', line, col);
        }
      }
      const value = Number(source.slice(i, j));
      if (!Number.isFinite(value)) {
        throw new ExprParseError(`Invalid number literal "${source.slice(i, j)}"`, line, col);
      }
      tokens.push({ kind: 'num', value, col });
      i = j;
      continue;
    }

    // identifier — letters and underscore, then letters/digits/underscore
    if ((c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a) || c === 0x5f) {
      let j = i + 1;
      while (j < len) {
        const cc = source.charCodeAt(j);
        if (
          (cc >= 0x41 && cc <= 0x5a) ||
          (cc >= 0x61 && cc <= 0x7a) ||
          (cc >= 0x30 && cc <= 0x39) ||
          cc === 0x5f
        ) j++;
        else break;
      }
      const value = source.slice(i, j);
      tokens.push({ kind: 'ident', value, col });
      i = j;
      continue;
    }

    // 2-char punct
    if (i + 1 < len) {
      const two = source.slice(i, i + 2);
      if (PUNCT_2.indexOf(two) !== -1) {
        tokens.push({ kind: 'punct', value: two, col });
        i += 2;
        continue;
      }
    }

    // 1-char punct
    const one = source[i];
    if (PUNCT_1.indexOf(one) !== -1) {
      tokens.push({ kind: 'punct', value: one, col });
      i++;
      continue;
    }

    throw new ExprParseError(
      `Unexpected character "${one}" in expression`,
      line,
      col,
    );
  }

  tokens.push({ kind: 'eof', col: baseCol + len });
  return tokens;
}

/* ─────────────────────────── parser ───────────────────────────────── */

interface ParseCtx {
  tokens: ExprTok[];
  pos: number;
  line: number;
  limits: EtlLimits;
  depth: number;
  nodeCount: number;
}

function peek(ctx: ParseCtx): ExprTok {
  return ctx.tokens[ctx.pos];
}

function consume(ctx: ParseCtx): ExprTok {
  return ctx.tokens[ctx.pos++];
}

function matchPunct(ctx: ParseCtx, ...vs: string[]): boolean {
  const t = peek(ctx);
  return t.kind === 'punct' && vs.indexOf(t.value) !== -1;
}

function expectPunct(ctx: ParseCtx, v: string): ExprTok {
  const t = peek(ctx);
  if (t.kind !== 'punct' || t.value !== v) {
    throw new ExprParseError(
      `Expected "${v}" in expression, got ${describe(t)}`,
      ctx.line,
      t.col,
    );
  }
  return consume(ctx);
}

function describe(t: ExprTok): string {
  if (t.kind === 'eof') return '<end of expression>';
  if (t.kind === 'str') return `string "${t.value}"`;
  if (t.kind === 'num') return `number ${t.value}`;
  if (t.kind === 'ident') return `identifier "${t.value}"`;
  return `"${t.value}"`;
}

/**
 * Narrows an already-confirmed punctuation token down to its `.value`.
 * Always called after `matchPunct(...)` returned true; the runtime
 * `kind` check just appeases the type-checker without an unsafe cast.
 */
function punctValue(t: ExprTok): string {
  if (t.kind !== 'punct') {
    throw new ExprParseError(
      `internal: expected punctuation token, got ${describe(t)}`,
      0,
      t.col,
    );
  }
  return t.value;
}

function bumpDepth(ctx: ParseCtx, t: ExprTok): void {
  if (++ctx.depth > ctx.limits.maxExprDepth) {
    throw new ExprParseError(
      `expression nesting exceeds ${ctx.limits.maxExprDepth}`,
      ctx.line,
      t.col,
    );
  }
}

function dropDepth(ctx: ParseCtx): void {
  ctx.depth--;
}

function bumpNodes(ctx: ParseCtx, t: ExprTok): void {
  if (++ctx.nodeCount > ctx.limits.maxExprNodes) {
    throw new ExprParseError(
      `expression has more than ${ctx.limits.maxExprNodes} nodes`,
      ctx.line,
      t.col,
    );
  }
}

function parseOr(ctx: ParseCtx): Expr {
  let left = parseAnd(ctx);
  while (matchPunct(ctx, '||')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const right = parseAnd(ctx);
    bumpNodes(ctx, tok);
    left = { kind: 'binary', op: '||', left, right };
    dropDepth(ctx);
  }
  return left;
}

function parseAnd(ctx: ParseCtx): Expr {
  let left = parseNot(ctx);
  while (matchPunct(ctx, '&&')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const right = parseNot(ctx);
    bumpNodes(ctx, tok);
    left = { kind: 'binary', op: '&&', left, right };
    dropDepth(ctx);
  }
  return left;
}

function parseNot(ctx: ParseCtx): Expr {
  if (matchPunct(ctx, '!')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const arg = parseNot(ctx);
    bumpNodes(ctx, tok);
    dropDepth(ctx);
    return { kind: 'unary', op: '!', arg };
  }
  return parseCmp(ctx);
}

function parseCmp(ctx: ParseCtx): Expr {
  const left = parseAdd(ctx);
  if (matchPunct(ctx, '==', '!=', '<', '<=', '>', '>=')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const right = parseAdd(ctx);
    bumpNodes(ctx, tok);
    dropDepth(ctx);
    return { kind: 'binary', op: punctValue(tok) as ExprBinaryOp, left, right };
  }
  return left;
}

function parseAdd(ctx: ParseCtx): Expr {
  let left = parseMul(ctx);
  while (matchPunct(ctx, '+', '-')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const right = parseMul(ctx);
    bumpNodes(ctx, tok);
    dropDepth(ctx);
    left = { kind: 'binary', op: punctValue(tok) as ExprBinaryOp, left, right };
  }
  return left;
}

function parseMul(ctx: ParseCtx): Expr {
  let left = parseUnary(ctx);
  while (matchPunct(ctx, '*', '/', '%')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const right = parseUnary(ctx);
    bumpNodes(ctx, tok);
    dropDepth(ctx);
    left = { kind: 'binary', op: punctValue(tok) as ExprBinaryOp, left, right };
  }
  return left;
}

function parseUnary(ctx: ParseCtx): Expr {
  if (matchPunct(ctx, '-')) {
    const tok = consume(ctx);
    bumpDepth(ctx, tok);
    const arg = parseUnary(ctx);
    bumpNodes(ctx, tok);
    dropDepth(ctx);
    return { kind: 'unary', op: '-' as ExprUnaryOp, arg };
  }
  return parsePostfix(ctx);
}

function parsePostfix(ctx: ParseCtx): Expr {
  let node = parsePrimary(ctx);
  while (true) {
    if (matchPunct(ctx, '.')) {
      const dot = consume(ctx);
      const t = peek(ctx);
      if (t.kind !== 'ident') {
        throw new ExprParseError(
          `Expected property name after ".", got ${describe(t)}`,
          ctx.line,
          t.col,
        );
      }
      consume(ctx);
      bumpNodes(ctx, dot);
      node = { kind: 'member', obj: node, prop: t.value };
      continue;
    }
    if (matchPunct(ctx, '[')) {
      const open = consume(ctx);
      bumpDepth(ctx, open);
      const key = parseOr(ctx);
      expectPunct(ctx, ']');
      bumpNodes(ctx, open);
      dropDepth(ctx);
      node = { kind: 'index', obj: node, key };
      continue;
    }
    if (matchPunct(ctx, '(')) {
      // Function call. Only valid if `node` is a bare identifier whose
      // name is in the builtin whitelist; otherwise reject — we don't
      // support method-call syntax (`a.b(c)`) inside expressions.
      const open = consume(ctx);
      if (node.kind !== 'ident') {
        throw new ExprParseError(
          'function call is only allowed on a bare builtin name',
          ctx.line,
          open.col,
        );
      }
      const name = node.name;
      if (!EXPR_BUILTIN_FNS.has(name as ExprBuiltinFn)) {
        throw new ExprParseError(
          `unknown function "${name}" — allowed: ${[...EXPR_BUILTIN_FNS].join(', ')}`,
          ctx.line,
          open.col,
        );
      }
      const args: Expr[] = [];
      bumpDepth(ctx, open);
      if (!matchPunct(ctx, ')')) {
        args.push(parseOr(ctx));
        while (matchPunct(ctx, ',')) {
          consume(ctx);
          args.push(parseOr(ctx));
        }
      }
      expectPunct(ctx, ')');
      dropDepth(ctx);
      const fn = name as ExprBuiltinFn;
      const minArity = EXPR_BUILTIN_ARITY[fn];
      // `can(action, resource?)` accepts 1 or 2 args; treat the table as min.
      if (args.length < minArity) {
        throw new ExprParseError(
          `${fn}() expects at least ${minArity} arg(s), got ${args.length}`,
          ctx.line,
          open.col,
        );
      }
      bumpNodes(ctx, open);
      node = { kind: 'call', fn, args };
      continue;
    }
    break;
  }
  return node;
}

function parsePrimary(ctx: ParseCtx): Expr {
  const t = peek(ctx);
  if (t.kind === 'num') {
    consume(ctx);
    bumpNodes(ctx, t);
    return { kind: 'lit', value: t.value };
  }
  if (t.kind === 'str') {
    consume(ctx);
    bumpNodes(ctx, t);
    return { kind: 'lit', value: t.value };
  }
  if (t.kind === 'ident') {
    consume(ctx);
    bumpNodes(ctx, t);
    if (Object.prototype.hasOwnProperty.call(KEYWORD_LITERALS, t.value)) {
      return { kind: 'lit', value: KEYWORD_LITERALS[t.value] };
    }
    return { kind: 'ident', name: t.value };
  }
  if (t.kind === 'punct' && t.value === '(') {
    consume(ctx);
    bumpDepth(ctx, t);
    const inner = parseOr(ctx);
    expectPunct(ctx, ')');
    dropDepth(ctx);
    return inner;
  }
  throw new ExprParseError(
    `Expected literal, identifier, or "(", got ${describe(t)}`,
    ctx.line,
    t.col,
  );
}

/* ─────────────────────────── public API ───────────────────────────── */

export interface ParseExprOptions {
  /** Source line number (1-based) used for error reporting. */
  readonly line?: number;
  /** Column offset of the start of `source` within its surrounding line. */
  readonly column?: number;
  readonly limits?: EtlLimits;
}

/**
 * Parses an expression source slice and returns its {@link Expr} AST.
 *
 * Throws {@link ExprParseError} on grammar violation. The expression must
 * consume the entire input — trailing tokens are an error.
 */
export function parseExpr(source: string, options: ParseExprOptions = {}): Expr {
  const limits = options.limits ?? DEFAULT_LIMITS;
  const line = options.line ?? 0;
  const baseCol = options.column ?? 1;
  const tokens = tokenizeExpr(source, line, baseCol);
  const ctx: ParseCtx = { tokens, pos: 0, line, limits, depth: 0, nodeCount: 0 };
  const expr = parseOr(ctx);
  const tail = peek(ctx);
  if (tail.kind !== 'eof') {
    throw new ExprParseError(
      `Unexpected ${describe(tail)} after expression`,
      line,
      tail.col,
    );
  }
  return expr;
}

/**
 * Non-throwing variant. Returns `null` on error rather than throwing,
 * suitable for tooling that wants to surface diagnostics without halting.
 */
export function parseExprSafe(
  source: string,
  options: ParseExprOptions = {},
): { ok: true; value: Expr } | { ok: false; error: ExprParseError } {
  try {
    return { ok: true, value: parseExpr(source, options) };
  } catch (e) {
    if (e instanceof ExprParseError) return { ok: false, error: e };
    throw e;
  }
}

/**
 * Parses a template string with `${…}` interpolation markers into an
 * {@link ExprTemplate}. Backslash-escaped markers are literal:
 * `"\${not-evaluated}"` survives as a string fragment.
 *
 * Returns `null` when the source contains no markers — the caller can
 * skip wrapping a plain string in a template node and keep the v1 fast
 * path.
 */
export function parseTemplate(source: string, options: ParseExprOptions = {}): Expr | null {
  // Quick check — most narrations have no interpolation.
  if (!source.includes('${')) return null;

  const parts: Array<string | Expr> = [];
  const limits = options.limits ?? DEFAULT_LIMITS;
  const line = options.line ?? 0;
  const baseCol = options.column ?? 1;

  let i = 0;
  let buf = '';
  const flush = (): void => { if (buf.length > 0) { parts.push(buf); buf = ''; } };

  while (i < source.length) {
    const c = source[i];

    // Backslash-escape: `\${...}` becomes a literal `${...}` fragment.
    if (c === '\\' && source[i + 1] === '$' && source[i + 2] === '{') {
      buf += '${';
      i += 3;
      continue;
    }

    if (c === '$' && source[i + 1] === '{') {
      flush();
      // Find the matching closing brace, respecting nesting and quoted
      // strings inside the expression.
      let depth = 1;
      let j = i + 2;
      let inStr = false;
      while (j < source.length && depth > 0) {
        const cc = source[j];
        if (inStr) {
          if (cc === '\\' && j + 1 < source.length) { j += 2; continue; }
          if (cc === '"') inStr = false;
          j++;
          continue;
        }
        if (cc === '"') { inStr = true; j++; continue; }
        if (cc === '{') depth++;
        else if (cc === '}') depth--;
        if (depth === 0) break;
        j++;
      }
      if (depth !== 0) {
        throw new ExprParseError(
          'unterminated `${...}` interpolation',
          line,
          baseCol + i,
        );
      }
      const exprSrc = source.slice(i + 2, j);
      const exprAst = parseExpr(exprSrc, {
        line,
        column: baseCol + i + 2,
        limits,
      });
      parts.push(exprAst);
      i = j + 1; // skip the `}`
      continue;
    }

    buf += c;
    i++;
  }

  flush();

  // Edge case: caller-provided source had `${` but it was inside an
  // escape — return null so the string is treated as plain text.
  if (parts.length === 1 && typeof parts[0] === 'string' && parts[0] === source) {
    return null;
  }

  return { kind: 'tpl', parts };
}
